<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import type { EmblaCarouselType } from "embla-carousel";
import emblaCarouselVue from "embla-carousel-vue";
import { getObservationList, getSpecieDetail } from "@carnet/api/backoffice";
import { apiBool, throwOnNotFound } from "@/utils/api";
import { apiRadiusKm } from "@carnet/ui/utils/location";
import { parseSiteIdQuery, parsePhotoIndexParam } from "@carnet/ui/utils/observationPhotosQuery";

definePageMeta({
  name: "observations.specie.photo",
  layout: "blank",
  key: (route) => `observations-specie-${route.params.specieId}-photo`,
});

const FETCH_BUFFER = 3;
const KEEP_BUFFER = 10;

const { t } = useI18n();
const router = useRouter();
const localeRoute = useLocaleRoute();
const getRouteBaseName = useRouteBaseName();
const route = useRoute("observations.specie.photo");

const specieId = computed(() => Number(route.params.specieId));

// Chemin interne uniquement ("//" et "/\" ouvriraient une redirection externe).
const backTarget = computed(() => {
  const raw = route.query.from;
  const v = Array.isArray(raw) ? raw[0] : raw;
  return v && /^\/(?![/\\])/.test(v) ? v : undefined;
});
const backIsDefault = computed(() => !backTarget.value || getRouteBaseName(router.resolve(backTarget.value)) === "observations.specie");
const backLink = computed(
  () => backTarget.value ?? localeRoute({ name: "observations.specie", params: { specieId: specieId.value }, query: route.query })
);
const backLabel = computed(() => (backIsDefault.value ? t("observations.species.photos.viewer.back") : t("ui.back")));
const requestedIndex = parsePhotoIndexParam(route.params.photoIndex);
const targetIndex = ref(requestedIndex);
const currentIndex = ref(requestedIndex);

const { data: specieData, error: specieError } = await getSpecieDetail({
  composable: "useFetch",
  key: `observations-specie-${specieId.value}`,
  path: { locale: appLocale(), id: String(specieId.value) },
});

throwOnNotFound(specieError.value, "Espèce introuvable");

const mineOnly = useMineFilter();
const { filter: locationFilter } = useUrlLocationFilter({ withRadius: true });

const filterQuery = computed(() => ({
  specieId: specieId.value,
  sort: "date_desc" as const,
  withPhotoOnly: apiBool(true),
  mineOnly: apiBool(mineOnly.value),
  siteId: parseSiteIdQuery(route.query.siteId),
  "center[latitude]": locationFilter.value.center?.[1],
  "center[longitude]": locationFilter.value.center?.[0],
  radius: apiRadiusKm(locationFilter.value),
}));

const {
  data: initialData,
  status,
  error,
  refresh,
} = await getObservationList({
  composable: "useFetch",
  key: `observations-photo-viewer-${requestedIndex}`,
  path: { locale: appLocale() },
  query: {
    page: requestedIndex,
    size: 1,
    ...filterQuery.value,
  },
});

type Observation = NonNullable<NonNullable<typeof initialData.value>["data"]["list"]>[number];

const total = computed(() => initialData.value?.data?.pagination?.total ?? 0);
const loadedObservations = reactive(new Map<number, Observation>());
const decodedPhotoIndices = reactive(new Set<number>());
const pendingFetches = new Set<number>();

function markPhotoDecoded(idx: number) {
  decodedPhotoIndices.add(idx);
}

watchEffect(() => {
  const initial = initialData.value?.data?.list?.[0];
  if (initial) {
    loadedObservations.set(requestedIndex, initial);
  }
});

const slideIndices = computed(() => (total.value > 0 ? Array.from({ length: total.value }, (_, i) => i + 1) : []));

const isInFetchWindow = (idx: number) => {
  const lo = Math.min(currentIndex.value, targetIndex.value) - FETCH_BUFFER;
  const hi = Math.max(currentIndex.value, targetIndex.value) + FETCH_BUFFER;
  return idx >= lo && idx <= hi;
};

const isInKeepWindow = (idx: number) => {
  const lo = Math.min(currentIndex.value, targetIndex.value) - KEEP_BUFFER;
  const hi = Math.max(currentIndex.value, targetIndex.value) + KEEP_BUFFER;
  return idx >= lo && idx <= hi;
};

async function fetchObservation(idx: number): Promise<void> {
  if (loadedObservations.has(idx)) return;
  if (pendingFetches.has(idx)) return;
  if (idx < 1 || idx > total.value) return;

  pendingFetches.add(idx);
  try {
    const result = await getObservationList({
      composable: "$fetch",
      path: { locale: appLocale() },
      query: {
        page: idx,
        size: 1,
        ...filterQuery.value,
      },
    });
    const obs = result?.data?.list?.[0];
    if (!obs) return;
    if (!isInKeepWindow(idx)) return;
    if (loadedObservations.has(idx)) return;
    loadedObservations.set(idx, obs);
  } catch (e) {
    console.error("Failed to load observation at index", idx, e);
  } finally {
    pendingFetches.delete(idx);
  }
}

function fetchBuffer(centerIndex: number) {
  if (total.value === 0) return;
  const min = Math.max(1, centerIndex - FETCH_BUFFER);
  const max = Math.min(total.value, centerIndex + FETCH_BUFFER);
  for (let idx = min; idx <= max; idx++) {
    if (!loadedObservations.has(idx)) fetchObservation(idx);
  }
}

function pruneBuffer(centerIndex: number) {
  if (total.value === 0) return;
  const min = Math.max(1, centerIndex - KEEP_BUFFER);
  const max = Math.min(total.value, centerIndex + KEEP_BUFFER);
  const toDelete: number[] = [];
  for (const idx of loadedObservations.keys()) {
    if (idx < min || idx > max) toDelete.push(idx);
  }
  for (const idx of toDelete) {
    loadedObservations.delete(idx);
    decodedPhotoIndices.delete(idx);
  }
}

const [emblaRef, emblaApi] = emblaCarouselVue({
  align: "center",
  containScroll: "trimSnaps",
  duration: 15,
  startIndex: requestedIndex - 1,
  watchDrag: (api) => api.scrollSnapList().length > 1,
});

const canScrollPrev = ref(false);
const canScrollNext = ref(false);

function syncCanScroll(api: EmblaCarouselType) {
  canScrollPrev.value = api.canScrollPrev();
  canScrollNext.value = api.canScrollNext();
}

function syncTargetIndex(api: EmblaCarouselType) {
  const newIndex = api.selectedScrollSnap() + 1;
  if (newIndex !== targetIndex.value) {
    targetIndex.value = newIndex;
  }
}

function syncCurrentIndex(api: EmblaCarouselType) {
  const newIndex = api.selectedScrollSnap() + 1;
  if (newIndex !== currentIndex.value) {
    currentIndex.value = newIndex;
  }
}

watch(emblaApi, (api) => {
  if (!api) return;
  syncCanScroll(api);
  syncTargetIndex(api);
  syncCurrentIndex(api);
  api.on("select", (a) => {
    syncCanScroll(a);
    syncTargetIndex(a);
    syncCurrentIndex(a);
  });
  api.on("reInit", (a) => {
    syncCanScroll(a);
    syncTargetIndex(a);
    syncCurrentIndex(a);
  });
});

let isUnmounted = false;

const debouncedFetchBuffer = useDebounceFn((idx: number) => {
  if (isUnmounted) return;
  fetchBuffer(idx);
}, 150);
const debouncedSettle = useDebounceFn((idx: number) => {
  if (isUnmounted) return;
  pruneBuffer(idx);
  if (getRouteBaseName(route) !== "observations.specie.photo") return;
  router
    .replace(
      localeRoute({
        name: "observations.specie.photo",
        params: { specieId: specieId.value, photoIndex: idx },
        query: route.query,
      })
    )
    .catch(() => {});
}, 150);

onMounted(() => {
  fetchBuffer(targetIndex.value);
  window.addEventListener("keydown", onKey);

  watch(targetIndex, (newIdx) => {
    debouncedFetchBuffer(newIdx);
  });

  watch(currentIndex, (newIdx) => {
    debouncedSettle(newIdx);
  });

  watch(
    () => route.params.photoIndex,
    (newIdxParam) => {
      const newIdx = parsePhotoIndexParam(newIdxParam);
      if (newIdx !== currentIndex.value && emblaApi.value) {
        emblaApi.value.scrollTo(newIdx - 1);
      }
    }
  );
});

onBeforeUnmount(() => {
  isUnmounted = true;
  window.removeEventListener("keydown", onKey);
});

function scrollPrev() {
  emblaApi.value?.scrollPrev(true);
}
function scrollNext() {
  emblaApi.value?.scrollNext(true);
}

function onKey(e: KeyboardEvent) {
  if (e.key === "ArrowLeft") scrollPrev();
  else if (e.key === "ArrowRight") scrollNext();
  else if (e.key === "Escape") router.push(backLink.value);
}

const currentObservation = computed(() => loadedObservations.get(targetIndex.value));
const specie = computed(() => specieData.value?.data);

function authorName(member: { fullName?: string | null; firstName: string; lastName: string }): string {
  return memberFullName(member);
}

const downloadLabel = computed(() => {
  if (!currentObservation.value) return t("observations.species.photos.viewer.download");
  const { extension, size } = currentObservation.value.downloadPhoto;
  const formattedExtension = extension.startsWith(".") ? extension : `.${extension}`;
  return `${t("observations.species.photos.viewer.download")} ${t("observations.species.photos.viewer.meta", { extension: formattedExtension, size: formatFileSize(size) })}`;
});

const observedSentence = computed(() => {
  if (!currentObservation.value) return "";
  const date = formatObservationDate(currentObservation.value.observationDate);
  const author = authorName(currentObservation.value.participation.member);
  const site = (currentObservation.value.site as { id: number; name?: string } | null)?.name;
  return site
    ? t("observations.species.photos.viewer.observed.withSite", { date, site, author })
    : t("observations.species.photos.viewer.observed.noSite", { date, author });
});

const specieName = computed(() => specie.value?.commonName ?? t("species.title"));

useSeoMeta({
  title: () =>
    t("observations.species.photos.viewer.pageTitle", {
      name: specieName.value,
      current: currentIndex.value,
      total: total.value,
    }),
  description: () => metaDescription(observedSentence.value) ?? t("seo.observations.specie.description", { name: specieName.value }),
});
useSeoImage(() => seoImageFrom(currentObservation.value?.photo, specieName.value));
</script>

<template>
  <div class="fixed inset-0 bg-inverted text-inverted flex flex-col">
    <header class="flex items-center px-4 md:px-10 pt-6 pb-3 shrink-0">
      <UButton variant="link" icon="icon:back" :to="backLink">
        {{ backLabel }}
      </UButton>
    </header>

    <div class="relative flex-1 min-h-0 flex">
      <span
        v-if="status === 'pending' && slideIndices.length === 0"
        class="m-auto motion-safe:animate-spin block size-12 rounded-full border-2 border-white/30 border-t-white"
        aria-hidden="true"
      />

      <div v-else-if="error" class="m-auto py-8 flex flex-col items-center gap-y-4">
        <AppMessageWrapper type="error">{{ t("error.loading") }}</AppMessageWrapper>
        <UButton variant="outline" color="primary" @click="refresh()">{{ t("error.retry") }}</UButton>
      </div>

      <p v-else-if="total === 0" class="m-auto text-white/80">
        {{ t("observations.species.photos.viewer.notFound") }}
      </p>

      <template v-else>
        <div
          ref="emblaRef"
          class="size-full overflow-hidden"
          role="region"
          aria-roledescription="carousel"
          :aria-label="t('observations.species.photos.viewer.title')"
        >
          <ul class="h-full flex">
            <li
              v-for="idx in slideIndices"
              :key="idx"
              class="relative select-none h-full basis-full min-w-0 shrink-0 flex items-center justify-center"
              role="group"
              aria-roledescription="slide"
              :aria-label="t('observations.species.photos.viewer.position', { current: idx, total })"
            >
              <AppImage
                v-if="loadedObservations.get(idx)"
                :src="loadedObservations.get(idx)!.photo.url"
                :alt="t('observations.species.photos.viewer.imageAlt', { name: specie?.commonName ?? '', current: idx, total })"
                variant="species"
                loading="eager"
                :fetchpriority="idx === requestedIndex ? 'high' : 'auto'"
                img-class="max-h-full max-w-full object-contain"
                @load="markPhotoDecoded(idx)"
                @error="markPhotoDecoded(idx)"
              />
              <span
                v-if="isInFetchWindow(idx) && !decodedPhotoIndices.has(idx)"
                class="absolute inset-0 m-auto motion-safe:animate-spin size-6 rounded-full border-2 border-white/30 border-t-white"
                aria-hidden="true"
              />
            </li>
          </ul>
        </div>

        <AppCarouselButton
          v-if="canScrollPrev"
          direction="prev"
          :aria-label="t('observations.species.photos.viewer.previous')"
          class="max-md:hidden absolute left-10 top-1/2 -translate-y-1/2 bg-blue-800/50"
          @click="scrollPrev"
        />

        <AppCarouselButton
          v-if="canScrollNext"
          direction="next"
          :aria-label="t('observations.species.photos.viewer.next')"
          class="max-md:hidden absolute right-10 top-1/2 -translate-y-1/2 bg-blue-800/50"
          @click="scrollNext"
        />
      </template>
    </div>

    <footer v-if="total || observedSentence" class="border-t border-blue-600 px-4 md:px-10 py-6 shrink-0 flex flex-col gap-y-6">
      <div class="flex flex-col md:flex-row items-start justify-between gap-x-4 gap-y-2">
        <p class="text-sm" aria-live="polite" aria-atomic="true">
          <span v-if="total > 1">
            {{ t("observations.species.photos.viewer.position", { current: targetIndex, total }) }}
          </span>
          <span v-if="observedSentence">&ensp;{{ observedSentence }} </span>
        </p>

        <UButton
          :disabled="!currentObservation"
          variant="link"
          :href="currentObservation?.downloadPhoto.url"
          :download="currentObservation ? `${specie?.commonName ?? 'photo'}-${currentObservation.id}.jpg` : undefined"
          target="_blank"
          rel="noopener noreferrer"
          trailing-icon="icon:download"
          class="whitespace-nowrap shrink-0"
        >
          {{ downloadLabel }}
        </UButton>
      </div>
      <div class="md:hidden flex justify-end shrink-0 gap-2">
        <AppCarouselButton
          :disabled="!canScrollPrev"
          direction="prev"
          :aria-label="t('observations.species.photos.viewer.previous')"
          @click="scrollPrev"
        />

        <AppCarouselButton
          :disabled="!canScrollNext"
          direction="next"
          :aria-label="t('observations.species.photos.viewer.next')"
          @click="scrollNext"
        />
      </div>
    </footer>
  </div>
</template>
